import { match } from 'ts-pattern'

import { SyncErrorTaskInput } from './types'

export const hasQuickbooksSettingsAction = (
  input: SyncErrorTaskInput
): boolean =>
  input.externalService === 'QuickBooks_Online' &&
  [
    'QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED',
    'QUICKBOOKS_MULTI_CURRENCY_DISABLED'
  ].includes(input.errorCode)

export const isQuickbooksCreditNoteApplicationImport = (
  input: SyncErrorTaskInput
): boolean =>
  input.externalService === 'QuickBooks_Online' &&
  input.direction === 'PULL' &&
  [
    'QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED',
    'QUICKBOOKS_CREDIT_NOTE_APPLICATION_AMBIGUOUS'
  ].includes(input.errorCode)

export const quickbooksSyncErrorContent = (input: SyncErrorTaskInput) => {
  if (input.externalService !== 'QuickBooks_Online') return undefined

  return match(input.errorCode)
    .with('QUICKBOOKS_PAYMENT_DECLINED', () => ({
      summary: 'Sequence could not fully apply the QuickBooks payment',
      explanation: input.errorMessage,
      instruction:
        'Review the payment and affected invoices in both systems. Contact support to retry this import.'
    }))
    .with('QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED', () => ({
      summary: 'Automatically apply credits is turned on in QuickBooks',
      explanation: isQuickbooksCreditNoteApplicationImport(input)
        ? 'Sequence couldn\'t import the credit note applications on this QuickBooks payment because "Automatically apply credits" is turned on.'
        : `Sequence couldn't sync this ${input.entityType.toLowerCase().replaceAll('_', ' ')} because "Automatically apply credits" is turned on in QuickBooks.`,
      instruction:
        'Turn off "Automatically apply credits" under Account and settings → Advanced → Automation in QuickBooks. ' +
        (input.direction === 'PULL'
          ? 'Contact support to retry this import.'
          : 'Then sync from this notification.')
    }))
    .with('QUICKBOOKS_MULTI_CURRENCY_DISABLED', () => ({
      summary: 'QuickBooks Multicurrency is turned off',
      explanation: input.errorMessage,
      instruction:
        'Review the Currency settings under Account and settings → Advanced in QuickBooks before syncing again.'
    }))
    .with('QUICKBOOKS_INVOICE_CURRENCY_MISMATCH', () => ({
      summary: 'Invoice currency differs from the payment currency',
      explanation: input.errorMessage,
      instruction:
        'Review the invoice and payment details before trying to sync again.'
    }))
    .with('QUICKBOOKS_INVOICE_GROSS_TOTAL_MISMATCH', () => ({
      summary: 'Invoice totals differ between Sequence and QuickBooks',
      explanation: input.errorMessage,
      instruction: 'Review the invoice totals in both systems.'
    }))
    .with('QUICKBOOKS_INVOICE_BALANCE_MISMATCH', () => ({
      summary: 'Syncing the payment would leave different invoice balances',
      explanation: input.errorMessage,
      instruction:
        'Review the invoice payments and credit note applications in both systems.'
    }))
    .with('QUICKBOOKS_CREDIT_NOTE_APPLICATION_AMBIGUOUS', () => ({
      summary:
        'QuickBooks does not identify how credits are split across invoices',
      explanation:
        "Sequence couldn't import this payment's credit note applications. It applies several credit notes across several invoices, and QuickBooks doesn't say how much of each credit note went to each invoice.",
      instruction:
        'Apply each credit note in its own payment in QuickBooks. Contact support if the import remains unresolved.'
    }))
    .otherwise(() => undefined)
}
